/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {IHorizontalTab} from "../../../components/Tabs/VerticalHorizontalTabs/VerticalHorizontalTabs";
import ArgosFlexbox from "../../../components/Containers/ArgosFlexbox";
import {TabContext, TabList, TabPanel} from "@mui/lab";
import * as React from "react";
import {useCallback, useEffect, useMemo, useState} from "react";
import {IconButton, Tab, Tooltip, Typography} from "@mui/material";
import {MRT_Cell, MRT_ColumnDef} from "material-react-table";
import {tableHeaderGray} from "../../../../constants/colors";
import Plotly2DTracesTop from "../Plots/Plotly2DTracesTop";
import Plotly2DTracesFront from "../Plots/Plotly2DTracesFront";
import Plotly2DTracesSide from "../Plots/Plotly2DTracesSide";
import Plotly3D from "../Plots/Plotly3D";
import {SensorPositionRow} from "./constants";
import useGetUrlParams from "../../../../hooks/useGetUrlParams";
import cloneDeep from "lodash/cloneDeep";
import {useAppDispatch, useAppSelector} from "../../../../redux/hooks";
import {selectCoordinateSystemName, selectVehicleProgramsItemDetailsState, setVehicleProgramsModifiedSensors} from "../../../../redux/ducks/VehicleProgramsDetails/slice";
import {ArgosWarningIcon} from "../../../components/Icons/ArgosActionIcons";

import {ArgosTableSimple} from "../../../components/Table/ArgosTableSimple";

const INNER_HORIZONTAL_POSITIONS_TAB_NAMES = ['Sensor Position Data Table', 'Sensor Position 2D View', 'Sensor Position 3D View']

const SensorPositionTab = (props: any) => {
  const {currentData} = props
  const coordinateSystemName = useAppSelector(state => selectCoordinateSystemName(state))
  let tempInnerHorizontalTabs: IHorizontalTab[] = []
  INNER_HORIZONTAL_POSITIONS_TAB_NAMES.forEach((innerTabName: string) => {
    const htab: IHorizontalTab = {
      'name': innerTabName
    }
    tempInnerHorizontalTabs.push(htab)
  })

  /**
   * Plotly data
   */
  const plotly2dData = currentData
  const plotly3dData = currentData?.sensors_data_map

  const [sensorPositionSelectedTab, setSensorPositionSelectedTab] = useState<number>(0)
  const stringSensorPositionSelectedTab = sensorPositionSelectedTab.toString()
  return <ArgosFlexbox style={{padding: 10, width: "100%"}}>
    <TabContext value={stringSensorPositionSelectedTab}>
      <ArgosFlexbox vertical style={{padding: 10, width: "100%"}}>
        <div>
          <Typography>Selected Coordinate System: {coordinateSystemName}</Typography>
          <TabList onChange={(event: React.SyntheticEvent, newValue: number) => {
            setSensorPositionSelectedTab(newValue)
            // setOpenTabIndex(0)
          }}>
            {tempInnerHorizontalTabs?.map((hTab: any, hTabIndex: number) => {
              return (
                <Tab key={hTabIndex} sx={{fontSize: 12, width: "100%", height: "100%"}} label={hTab?.name}
                     value={hTabIndex.toString()}/>
              )
            })}
          </TabList>
        </div>

        <div style={{flexGrow: 1, overflow: 'auto', height: "100%", width: "100%"}}>
          <TabPanel value={stringSensorPositionSelectedTab} style={{height: "100%", width: "100%"}}>
            {
              sensorPositionSelectedTab == 0 &&
              <SensorPositionsTablesTab currentData={currentData}/>
            }
            {
              sensorPositionSelectedTab == 1 &&
              <TabGraphs2d plotlyData={plotly2dData}/>
            }
            {
              sensorPositionSelectedTab == 2 &&
              <TabGraphs3d plotlyData={plotly3dData}/>
            }
          </TabPanel>
        </div>
      </ArgosFlexbox>
    </TabContext>
  </ArgosFlexbox>
}

const SensorPositionsTablesTab = (props: any) => {
  const {currentData} = props
  const sensorPositionTableData: any = useMemo(() => currentData?.["sensor_positions_table_data"] ?? {}, [currentData])
  return (
    <>
      {
        Object.keys(sensorPositionTableData).map((sensorPositionKey: string, index: number) =>
          <Tables tablesArray={sensorPositionTableData[sensorPositionKey]} tableIndex={index}
                  sensorPositionKey={sensorPositionKey} currentData={currentData}/>
        )
      }
    </>
  )
}

const Tables = (props: any) => {
  const {tablesArray, tableIndex, sensorPositionKey, currentData} = props
  return (
    <>
      <TableWrapper tableIndex={tableIndex} name={sensorPositionKey}
                    count={tablesArray.length}>
        <SubTable sensorPositionTableData={tablesArray} currentData={currentData}
                  sensorPositionKey={sensorPositionKey}/>
      </TableWrapper>
    </>
  )
}

const TableWrapper = (props: any) => {
  const {name, count, tableIndex} = props
  return (
    <ArgosFlexbox vertical>
      <Typography style={{paddingTop: tableIndex == 0 ? "0px" : "20px"}}>
        {name}s ({count})
      </Typography>
      {props.children}
    </ArgosFlexbox>
  )
}

const SubTable = (props: any) => {
  const dispatch = useAppDispatch()
  const {sensorPositionKey, sensorPositionTableData, currentData, fullTableData} = props
  const {developmentStageId, vehicleProgramId} = useGetUrlParams()

  const refAttenuation = React.useRef<any>({})
  const modifiedSensors = useAppSelector(state => selectVehicleProgramsItemDetailsState(state)?.modifiedSensors)
  useEffect(() => {
    if (modifiedSensors == null) return
    // refAttenuation.current = modifiedSensors ?? {}
  }, [modifiedSensors]);

  const [localTable, setLocalTable] = useState<any>(sensorPositionTableData)
  useEffect(() => {
    setLocalTable(sensorPositionTableData)
  }, [sensorPositionTableData]);

  const getCommonEditTextFieldProps = useCallback((
    cell: MRT_Cell<SensorPositionRow>,
  ): MRT_ColumnDef<SensorPositionRow>['muiEditTextFieldProps'] => {
    const rowIndex = cell.row.index
    const columnId = cell.column.id

    let value = 0
    const parentIndex = parseInt(cell.row.parentId ?? '0')
    const parentRow = localTable[parentIndex]
    const subRows = parentRow?.subRows
    const locationAbbreviation = getPropertyValueFromListOfRows(subRows, 'Location Abbreviation')
    const sensorId = parentRow.id
    const uniqueRowKey = `${sensorId}-${locationAbbreviation}-${parentIndex}`
    const subRow = parentRow?.subRows?.[rowIndex]

    // If subRow is attenuation, then get the attenuation value from the ref if it's not initial load
    if (subRow != null && subRow.propertyName === 'Attenuation') {
      // Check the value of this id in ref, and if it is not there, then use the attenuation value from the table
      if (refAttenuation.current?.[uniqueRowKey] == null) {
        const existingAttenuation = subRows[rowIndex][columnId]
        refAttenuation.current[uniqueRowKey] = {attenuation: existingAttenuation, updated: false, sensorId: sensorId}
        value = existingAttenuation
      } else {
        value = refAttenuation.current?.[uniqueRowKey]?.attenuation ?? 0
      }
    } else {
      value = cell.getValue<any>()
    }

    return {
      type: 'number',
      InputProps: {
        inputProps: {min: 0}
      },
      value: value,
      onChange: (e: any) => {
        e.target.value = e.target.value < 0 ? (e.target.value = 0) : e.target.value
        const attenuation = e.target.value
        refAttenuation.current[uniqueRowKey] = {attenuation, updated: true, sensorId, parentIndex}
      },
      onBlur: (e) => {
        const cloneTable = cloneDeep(localTable)
        const numAttenuation = Number(refAttenuation.current[uniqueRowKey]?.attenuation ?? 0)
        cloneTable[parentIndex].subRows[rowIndex][columnId] = numAttenuation
        setLocalTable(cloneTable)
        const map = {
          [uniqueRowKey]: {attenuation: numAttenuation, locationAbbreviation, sensorId}
        }

        dispatch(setVehicleProgramsModifiedSensors({
          type: 'update',
          data: map
        }))
      },
    };
  }, [currentData, localTable]);

  const getPropertyValueFromListOfRows = (row: any, propertyName: string) => {
    const property = row?.find((item: any) => item.propertyName === propertyName)
    return property?.value
  }

  const sensorPositionColumns: MRT_ColumnDef<SensorPositionRow>[] = [
    {
      id: 'sensorName',
      accessorFn: (row: SensorPositionRow) => row?.subRows ? row?.sensorName ?? '' : "",
      header: "Sensor Name",
      enableEditing: false,
      minSize: 80,
      size: 300,


      Cell: ({cell, row}) => {
        const id = row?.original?.sensorId
        const url = row.original.sensor_type == "Radar" ? `/radarPerformance?id=${id}` : `/cameraPerformance?id=${id}`

        // TODO: update query params for adas program id
        const adasProgramId = currentData?.["vehicle_measurements"]?.["adas_program_id"]
        const linkToAdasPrograms = `/adas-programs-details?adasProgramId=${adasProgramId}&htabs-1=1&htabs-1-name=Sensor%20Bill%20of%20Materials`
        return (
          <ArgosFlexbox style={{alignItems: "center"}}>
            {row.original.sensor_type != "IMU" && row.original.sensor_type != "Antenna" && <a onClick={() => window.open(url, "_blank")} href={url} target={'_blank'}>{cell.getValue<string>()}</a>}
            {row.original.sensor_type == "IMU" && cell.getValue<string>()}
            {row.original.sensor_type == "Antenna" && cell.getValue<string>()}
            {row.original.outOfStep &&
              <Tooltip
                title={`This sensor is different than the sensor allocated to this position in the ADAS Program BoM. Click on the icon to verify`}
                onClick={(e) => {
                  e.preventDefault();
                  window.open(linkToAdasPrograms, "_blank")
                }}>
                <IconButton>
                  <ArgosWarningIcon/>
                </IconButton>
              </Tooltip>}
          </ArgosFlexbox>

        )
      },
    },
    {
      header: "Property",
      accessorKey: "propertyName",
      enableEditing: false,
      minSize: 80,
      size: 300,

    },
    {
      header: "Value",
      accessorKey: "value",
      enableEditing: true,
      minSize: 80,
      size: 200,
      muiEditTextFieldProps: ({cell}: any) => ({
        ...getCommonEditTextFieldProps(cell),
        disabled: cell.row.original.propertyName != "Attenuation"
      }),
    },
    {
      header: "Units",
      accessorKey: "units",
      enableEditing: false,
      minSize: 80,
      size: 180,

    },
  ]

  return (
    <ArgosTableSimple
      // data
      columns={sensorPositionColumns}
      data={localTable}

      enableExpanding
      enablePagination={false}
      muiExpandButtonProps={({row}) => ({
        // Show expand button only when row has sub-elements
        sx: {
          display: row.subRows?.length ? 'flex' : 'none'
        }
      })}
      getSubRows={(originalRow) => originalRow?.subRows}
      // editing
      editDisplayMode="cell"
      enableEditing
      muiTableHeadRowProps={{
        // Header row style
        sx: {
          backgroundColor: tableHeaderGray,
        },
      }}
    />
  )
}

export const TabGraphs2d = (props: any) => {
  const {plotlyData} = props
  const dimensionsList = plotlyData?.vehicle_measurements?.dimensions_list
  const plotlyTraces = plotlyData?.sensors_data_map
  return (
    <ArgosFlexbox vertical style={{alignItems: "center", width: "100%", height: "100%"}}>
      <div style={{width: "100%", height: "100%", overflowX: 'auto'}}>
        <Plotly2DTracesTop plotlyTraces={plotlyTraces} dimensionsList={dimensionsList}/>
      </div>
      <div style={{width: "100%", height: "100%", overflowX: 'auto'}}>
        <Plotly2DTracesFront plotlyTraces={plotlyTraces} dimensionsList={dimensionsList}/>
      </div>
      <div style={{width: "100%", height: "100%", overflowX: 'auto'}}>
        <Plotly2DTracesSide plotlyTraces={plotlyTraces} dimensionsList={dimensionsList}/>
      </div>
    </ArgosFlexbox>
  )
}

export const TabGraphs3d = (props: any) => {
  const {plotlyData} = props
  return (
    <div style={{alignItems: "center", width: "100%", height: "100%", overflowX: 'auto'}}>
      <Plotly3D plotlyTraces={plotlyData}/>
    </div>
  )
}

export default SensorPositionTab